import React, { useState, useEffect } from "react";
import { getDeviceScreenshotUrl } from "../../utils/deviceScreenshot";

/**
 * LiveThumb — Canvas Service screenshot for a single device.
 *
 * Match the portal's exact cache-busting behaviour: a fresh UUIDv4 per
 * refresh cycle, used as the ?cachebust= query value, AND a `key` on
 * the <img> equal to that same UUID so React unmounts the previous img
 * element and mounts a new one. The unmount/remount is the belt to the
 * URL-change suspenders — some browsers will reuse a cached image from
 * the same DOM node even when src changes (we observed this on Chrome
 * with a strong CDN Cache-Control). Forcing a new element bypasses that.
 *
 * The `cacheBuster` prop coming in from the parent is just a "tick" —
 * its only role is to be different on each refresh so this component
 * knows to mint a new UUID. We don't put the parent's value directly
 * in the URL because timestamps from setInterval are predictable and
 * could theoretically share with other clients/probes; UUIDs from
 * crypto.randomUUID() are guaranteed unique.
 *
 * Props:
 *   envId        "dev" | "qa" | "prod"
 *   deviceId     hardware device id (e.g. "05K1Q3260095")
 *   cacheBuster  number | string — pass a changing value to trigger refresh
 *   orientation  "landscape" | "portrait" — controls thumbnail aspect.
 *                Canvases of either orientation upload natively-shaped
 *                JPGs to the CDN; without rotating our thumbnail
 *                container, portrait shots get squished into a
 *                landscape box. Surfaced via a `data-orientation`
 *                attribute so the consumer's CSS can swap dimensions.
 *   className    CSS class for the <img>
 *   alt          alt text
 */
function generateUuid() {
  // crypto.randomUUID() ships in all modern browsers and the Node test
  // env, but defend against ancient Safari just in case.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // RFC4122-ish fallback (good enough for cache busting, not for security)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Set ?VITE_DEBUG_LIVETHUMB=1 in the env (or localStorage.debug_livethumb = "1"
// in the browser console) to get console logs every refresh tick. Helps
// diagnose "is the image actually re-fetching?" without needing the
// network tab open.
function debugEnabled() {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("debug_livethumb") === "1") return true;
  } catch (_) { /* ignore SSR/private mode */ }
  return false;
}

export default function LiveThumb({ envId, deviceId, cacheBuster, orientation, className, alt }) {
  // The cacheBuster prop is a TICK SIGNAL only. We mint a fresh UUID per
  // tick and use it both for the URL query and the React key. Putting the
  // mint inside useState + useEffect-on-cacheBuster gives us "mint once
  // per tick", not on every re-render.
  const [bust, setBust] = useState(generateUuid);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    const next = generateUuid();
    if (debugEnabled()) {
      // eslint-disable-next-line no-console
      console.log(`[LiveThumb] ${deviceId} → new bust ${next.slice(0, 8)}…`);
    }
    setBust(next);
    setBroken(false);
  }, [cacheBuster, envId, deviceId]);

  if (!deviceId || broken) return null;

  const url = getDeviceScreenshotUrl(envId, deviceId, bust);

  // Normalize orientation values (CMS uses "portrait"/"landscape", but
  // some entries can come through as null/undefined or other casing).
  const orient = (orientation || "").toLowerCase() === "portrait" ? "portrait" : "landscape";

  return (
    <img
      // key on the img forces React to unmount the old element and mount
      // a new one when bust changes. Belt-and-braces against any browser
      // that would reuse the old image bytes for the same DOM node.
      key={bust}
      className={className}
      data-orientation={orient}
      // Expose the current bust as a data attribute so inspecting an
      // element shows whether the URL has been updating. If two ticks
      // apart inspections show the same data-bust, the React tick isn't
      // firing for this thumb.
      data-bust={bust.slice(0, 8)}
      src={url}
      alt={alt}
      onLoad={(e) => {
        if (debugEnabled()) {
          // eslint-disable-next-line no-console
          console.log(`[LiveThumb] ${deviceId} loaded ${e.target.naturalWidth}x${e.target.naturalHeight} (bust ${bust.slice(0, 8)}…)`);
        }
      }}
      onError={() => setBroken(true)}
    />
  );
}
